import { useState } from "react";
import type { PublicacaoDto } from "@base/contracts";
import { AlertCircle, CircleAlert, Eye, Trash2 } from "lucide-react";
import { EstadoVazio, MolduraDoApp } from "@base/ui";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  IconePlataforma,
  ROTULO_PLATAFORMA,
} from "../../../shared/branding/icone-plataforma";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { agruparPublicacoesAgenda } from "../domain/calendario-agenda";
import {
  useExcluirPublicacao,
  useFalhasPublicacoes,
  useReprocessarPublicacao,
} from "../application/use-publicacoes";
import { BotaoReprocessarFalha } from "./botao-reprocessar-falha";
import { ModalDetalheFalhaPublicacao } from "./modal-detalhe-falha-publicacao";

export function PaginaFalhasPublicacao() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("falhas-publicacao");
  const { data, isLoading, isError, error } = useFalhasPublicacoes();
  const reprocessar = useReprocessarPublicacao();
  const excluir = useExcluirPublicacao();
  const { avisar, confirmar } = useDialogos();
  const [detalhe, definirDetalhe] = useState<readonly PublicacaoDto[] | null>(
    null,
  );

  const grupos = agruparPublicacoesAgenda(data?.itens ?? []);

  async function aoReprocessar(ids: readonly string[]) {
    try {
      for (const id of ids) {
        await reprocessar.mutateAsync(id);
      }
      definirDetalhe(null);
      await avisar({
        titulo: ids.length > 1 ? "Voltaram para a fila" : "Voltou para a fila",
        descricao:
          ids.length > 1
            ? "Essas redes entram de novo na fila. Se der certo, aparecem em Publicados e ficam verdes na Agenda. Se falhar de novo, voltam para esta lista."
            : "O post entrou de novo na fila. Se der certo, aparece em Publicados e fica verde na Agenda. Se falhar de novo, volta para esta lista.",
      });
    } catch (erro) {
      await avisar({
        titulo: "Não deu para reprocessar",
        descricao:
          erro instanceof Error ? erro.message : "Tente de novo em instantes.",
      });
    }
  }

  async function aoExcluir(itens: readonly PublicacaoDto[]) {
    const ok = await confirmar({
      titulo:
        itens.length > 1 ? "Excluir estas falhas?" : "Excluir esta falha?",
      descricao:
        "Some da lista. Não publica de novo. Se quiser tentar outra vez, use Reprocessar.",
      rotuloConfirmar: "Excluir",
      rotuloCancelar: "Manter",
      tomConfirmar: "perigo",
    });
    if (!ok) return;
    try {
      await excluir.mutateAsync(itens.map((p) => p.id));
      definirDetalhe((atual) => {
        if (!atual) return null;
        const restam = atual.filter(
          (p) => !itens.some((excluida) => excluida.id === p.id),
        );
        return restam.length > 0 ? restam : null;
      });
      await avisar({
        titulo: "Falhas excluídas",
        descricao: "Os registros saíram da lista.",
      });
    } catch (erro) {
      await avisar({
        titulo: "Não deu para excluir",
        descricao:
          erro instanceof Error ? erro.message : "Tente de novo em instantes.",
      });
    }
  }

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Falhas de publicação"
      subtitulo="Posts que não saíram. Veja o erro, reprocesse ou tire da lista."
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
    >
      <PageLayout className="animate-aparecer space-y-6">
        <Alert>
          <AlertCircle />
          <AlertTitle>Como acompanhar o reprocessamento</AlertTitle>
          <AlertDescription>
            Ao clicar em Reprocessar, o post sai desta lista e vai para a fila.
            Sucesso: verde na Agenda e em{" "}
            <a
              href="#publicados-publicacao"
              className="cursor-pointer font-medium underline-offset-4 hover:underline"
            >
              Publicados
            </a>
            . Nova falha: volta para cá. Excluir só limpa o registro.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="grid gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : null}

        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Não carregou a lista</AlertTitle>
            <AlertDescription>
              {(error as Error)?.message ?? "Tente novamente."}
            </AlertDescription>
          </Alert>
        ) : null}

        {!isLoading && grupos.length === 0 ? (
          <EstadoVazio
            titulo="Nenhuma falha no momento"
            descricao="Quando a publicação falhar de vez, o post aparece aqui."
          />
        ) : null}

        <ul className="grid gap-3">
          {grupos.map((grupo) => {
            const titulo =
              grupo.representativo.nome.trim() ||
              grupo.representativo.legenda ||
              "(sem nome)";
            const motivo =
              grupo.itens.find((p) => p.motivoFalha?.trim())?.motivoFalha ??
              "Sem detalhe";
            return (
              <li
                key={grupo.chave}
                className="flex flex-col gap-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <CircleAlert
                      className="size-4 shrink-0 text-destructive"
                      aria-hidden
                    />
                    <p className="truncate text-sm font-medium text-foreground">
                      {titulo}
                    </p>
                    <span className="inline-flex items-center gap-1.5">
                      {grupo.itens.map((p) => (
                        <IconePlataforma
                          key={p.id}
                          plataforma={p.plataforma}
                          tom="falha"
                          className="size-3.5"
                          titulo={`${ROTULO_PLATAFORMA[p.plataforma]}: Falhou`}
                        />
                      ))}
                    </span>
                    <Badge variant="destructive">
                      {grupo.itens.length > 1
                        ? `${grupo.itens.length} redes`
                        : "Falhou"}
                    </Badge>
                  </div>
                  <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                    {motivo}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => definirDetalhe(grupo.itens)}
                  >
                    <Eye className="size-4" aria-hidden />
                    Ver erros
                  </Button>
                  <BotaoReprocessarFalha
                    itens={grupo.itens}
                    disabled={reprocessar.isPending}
                    onEscolher={(ids) => void aoReprocessar(ids)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer text-destructive hover:bg-destructive/10"
                    disabled={excluir.isPending}
                    onClick={() => void aoExcluir(grupo.itens)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    Excluir
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </PageLayout>

      <ModalDetalheFalhaPublicacao
        falhas={detalhe}
        onFechar={() => definirDetalhe(null)}
        reprocessando={reprocessar.isPending}
        onReprocessar={(ids) => {
          void aoReprocessar(ids);
        }}
      />
    </MolduraDoApp>
  );
}
